import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["aria-label"]
const _hoisted_2 = { class: "buttons-container" }

import ResumeBase from "@/Layouts/ResumeBase/ResumeBase.vue";
import TextInput from "@/components/Common/Inputs/TextInput/TextInput.vue";
import TextAreaInput from "@/components/Common/Inputs/TextAreaInput/TextAreaInput.vue";
import CheckboxInput from "@/components/Common/Inputs/CheckboxInput/CheckboxInput.vue";
import AppButton from "@/components/Common/AppButton/AppButton.vue";
import { useResumeStore, useErrorStore } from "@/store";
import { Qualification } from "@/store/models/resume.models";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, onBeforeRouteLeave, useRoute } from "vue-router";
import { onIonViewDidEnter } from "@ionic/vue";
import { handleBeforeUnload, focusOnMainContent } from "@/helpers";
import { IonInputCustomEvent } from "@ionic/core/components";
import { collateErrorsInDescription, validateDateFormat } from "@/helpers";


export default /*@__PURE__*/_defineComponent({
  __name: 'ResumeQualificationDetail',
  setup(__props) {

const { t } = useI18n<{}>({
  useScope: "global",
});
const resumeStore = useResumeStore();
const errorStore = useErrorStore();
const router = useRouter();
const route = useRoute();

const skipOnRouteBeforeLeave = ref(false);
const triggerDeleteModal = ref(false);

onBeforeRouteLeave(() => {
  resumeStore.setQualificationFormInit(false);
  window.removeEventListener("beforeunload", handleBeforeUnload);
});

onIonViewDidEnter(() => {
  focusOnMainContent(route?.meta?.mainContentId);
  skipOnRouteBeforeLeave.value = false;
  window.addEventListener("beforeunload", handleBeforeUnload);
});

onMounted(async () => {
  if (!resumeStore.resumeId) {
    await resumeStore.requestResume();
    if (!resumeStore.resumeId) {
      await resumeStore.createResume();
    }
  }
  const qualificationId = router.currentRoute.value.params.id as string;
  qualificationId !== "new"
    ? resumeStore.initQualificationForm(qualificationId)
    : resumeStore.initQualificationForm();
  initValidationMap();
});

const qualificationId = computed(() => {
  return resumeStore.qualificationFormData?.id;
});

const institution = computed(() => {
  return resumeStore.qualificationFormData?.institution;
});

const name = computed(() => {
  return resumeStore.qualificationFormData?.name;
});

const isQualificationComplete = computed(() => {
  return resumeStore.qualificationFormData?.isQualificationComplete;
});

const endDate = computed(() => {
  return resumeStore.qualificationFormData?.endDate;
});

const highlights = computed(() => {
  return resumeStore.qualificationFormData?.highlights;
});

const isFormSaving = ref(false);
const isQualificationDeleting = ref(false);

const setValue = <K extends keyof Qualification>(
  property: K,
  value: Qualification[K] | null | undefined
) => {
  if (value == null || !property) return;
  resumeStore.setQualificationFormValue(property, value);
};

const saveForm = async () => {
  skipOnRouteBeforeLeave.value = true;
  errorStore.setSaveFormError(false);
  try {
    isFormSaving.value = true;
    if (qualificationId.value) {
      await resumeStore.updateQualification(qualificationId.value);
    } else {
      await resumeStore.createQualification();
      // TODO: When do we declare that qualifications are completed?
      await resumeStore.updateResumeProperty("isQualificationsCompleted", true);
    }
    router.push({
      name: "ResumeQualifications",
    });
  } catch (error) {
    console.error("An error occured saving the form: ", error);
    errorStore.setSaveFormError(true);
  } finally {
    isFormSaving.value = false;
  }
};

const triggerDelete = () => {
  triggerDeleteModal.value = true;
};

const deleteQualification = async () => {
  try {
    skipOnRouteBeforeLeave.value = true;
    isQualificationDeleting.value = true;
    if (qualificationId.value) {
      await resumeStore.deleteQualification(qualificationId.value);
    } else {
      throw new Error("Something went wrong");
    }
    router.push({
      name: "ResumeQualifications",
    });
  } finally {
    isQualificationDeleting.value = false;
    triggerDeleteModal.value = false;
  }
};

const dismissDeleteModal = () => {
  triggerDeleteModal.value = false;
};

const onQualificationCompleteChange = (checked: boolean) => {
  setValue("isQualificationComplete", !checked);
  if (checked) {
    setValue("endDate", "");
    validationMap.value.set("endDate", true);
    const itemEl = document.querySelector(".end-date-input ion-item");
    if (itemEl) {
      itemEl.classList.remove("ion-invalid");
    }
    return;
  }
};

const errorIdPrefix = ref("error-message-disabler");
const saveButtonDescription = ref("");
const updateErrorDescription = () => {
  saveButtonDescription.value = collateErrorsInDescription(
    document,
    errorIdPrefix.value
  );
};

const returnToPage = () => {
  router.push({
    name: "ResumeQualifications",
  });
};
/* VALIDATION */

const rules = {
  name: {
    required: t("resume.qualificationForm.nameInput.isRequiredError"),
  },
  endDate: {
    invalidFormat: t(
      "resume.qualificationForm.endDateInput.invalidFormatError"
    ),
  },
  highlights: {
    characterLimit: t(
      "resume.qualificationForm.highlightsInput.characterLimitError"
    ),
  },
};

const validationMap = ref<Map<keyof Qualification, boolean>>(new Map([]));
const initValidationMap = () => {
  validationMap.value
    .set("name", name.value !== undefined && name.value.length > 0)
    .set("endDate", true);
};

const errorMessageMap = ref<Map<keyof Qualification, string>>(
  new Map([
    ["name", ""],
    ["endDate", ""],
    ["highlights", ""],
  ])
);

const setValidity = (
  property: keyof Qualification,
  valid: boolean,
  error?: string
): void => {
  validationMap.value.set(property, valid);
  errorMessageMap.value.set(property, error ?? "");
  return;
};

const isFormSavingEnabled = computed(() => {
  for (let isValid of validationMap.value.values()) {
    if (!isValid) return false;
  }
  return true;
});

const validateName = (event: IonInputCustomEvent<FocusEvent>) => {
  const itemEl = event.target.parentElement;
  itemEl?.classList.remove("ion-invalid");
  const name = event.target.value;
  if (!name) {
    itemEl?.classList.add("ion-invalid");
    setValidity("name", false, rules.name.required);
  } else {
    setValidity("name", true);
  }
};

const validateEndDate = (event: IonInputCustomEvent<FocusEvent>) => {
  const itemEl = event.target.parentElement;
  itemEl?.classList.remove("ion-invalid");
  if (!isQualificationComplete.value) {
    setValue("endDate", "");
    setValidity("endDate", true);
    return;
  }
  const date = event.target.value as string;
  if (!date) {
    setValidity("endDate", true);
    return;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (!validateDateFormat(date)) {
    itemEl?.classList.add("ion-invalid");
    setValidity("endDate", false, rules.endDate.invalidFormat);
  } else {
    setValidity("endDate", true);
  }
};

const validateHighlights = (event: IonInputCustomEvent<FocusEvent>) => {
  const itemEl = event.target.parentElement;
  itemEl?.classList.remove("ion-invalid");
  const highlights = event.target.value as string;
  if (highlights && highlights.length >= 600) {
    itemEl?.classList.add("ion-invalid");
    errorMessageMap.value.set("highlights", rules.highlights.characterLimit);
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ResumeBase, {
    breadcrumbs: [
      {
        route: 'my-qualifications',
        label: _unref(t)('resume.breadcrumbs.qualifications'),
        type: 'parent',
      },
      {
        label: _unref(t)('resume.breadcrumbs.qualificationDetails'),
      },
    ],
    mainContentId: "resume-qualification-detail-main-content",
    formSaveRetryFunction: saveForm,
    skipOnRouteBeforeLeave: skipOnRouteBeforeLeave.value,
    formDeleteFunction: deleteQualification,
    triggerDeleteModal: triggerDeleteModal.value,
    dismissDeleteModal: dismissDeleteModal
  }, {
    title: _withCtx(() => [
      _createTextVNode(_toDisplayString(_unref(t)("resume.qualificationForm.title")), 1)
    ]),
    description: _withCtx(() => [
      _createTextVNode(_toDisplayString(_unref(t)("resume.qualificationForm.description")), 1)
    ]),
    "main-content": _withCtx(() => [
      _createElementVNode("form", {
        "aria-label": _unref(t)('resume.qualificationForm.title')
      }, [
        _createVNode(TextInput, {
          "data-test": "name-input",
          errorText: errorMessageMap.value.get('name'),
          labelText: _unref(t)('resume.qualificationForm.nameInput.label'),
          placeholderText: _unref(t)('resume.qualificationForm.nameInput.placeholder'),
          value: name.value || '',
          isLoading: _unref(resumeStore).isResumeLoading,
          required: true,
          errorIdPrefix: errorIdPrefix.value,
          onOnChange: _cache[0] || (_cache[0] = 
            (event) => {
              validateName(event);
              setValue('name', event.detail.value);
            }
          ),
          onOnBlur: validateName,
          onOnUpdate: updateErrorDescription
        }, null, 8, ["errorText", "labelText", "placeholderText", "value", "isLoading", "errorIdPrefix"]),
        _createVNode(TextInput, {
          "data-test": "institution-input",
          labelText: _unref(t)('resume.qualificationForm.institutionInput.label'),
          placeholderText: 
            _unref(t)('resume.qualificationForm.institutionInput.placeholder')
          ,
          value: institution.value || '',
          isLoading: _unref(resumeStore).isResumeLoading,
          onOnChange: _cache[1] || (_cache[1] = (event) => setValue('institution', event.detail.value))
        }, null, 8, ["labelText", "placeholderText", "value", "isLoading"]),
        _createVNode(CheckboxInput, {
          "data-test": "is-completed-input",
          checked: !isQualificationComplete.value || false,
          labelText: _unref(t)('resume.qualificationForm.isCompletedInput.label'),
          isLoading: _unref(resumeStore).isResumeLoading,
          onOnChange: _cache[2] || (_cache[2] = (checked) => onQualificationCompleteChange(checked))
        }, null, 8, ["checked", "labelText", "isLoading"]),
        _createVNode(TextInput, {
          "data-test": "end-date-input",
          class: "end-date-input",
          errorText: errorMessageMap.value.get('endDate'),
          labelText: _unref(t)('resume.qualificationForm.endDateInput.label'),
          placeholderText: 
            _unref(t)('resume.qualificationForm.endDateInput.placeholder')
          ,
          value: endDate.value || '',
          isLoading: _unref(resumeStore).isResumeLoading,
          disabled: !isQualificationComplete.value,
          errorIdPrefix: errorIdPrefix.value,
          onOnChange: _cache[3] || (_cache[3] = (event) => setValue('endDate', event.detail.value)),
          onOnBlur: validateEndDate,
          onOnUpdate: updateErrorDescription
        }, null, 8, ["errorText", "labelText", "placeholderText", "value", "isLoading", "disabled", "errorIdPrefix"]),
        _createVNode(TextAreaInput, {
          "data-test": "description-input",
          labelText: _unref(t)('resume.qualificationForm.highlightsInput.label'),
          errorText: errorMessageMap.value.get('highlights'),
          maxLength: 600,
          placeholderText: 
            _unref(t)('resume.qualificationForm.highlightsInput.placeholder')
          ,
          autogrow: true,
          value: highlights.value || '',
          isLoading: _unref(resumeStore).isResumeLoading,
          onOnChange: _cache[4] || (_cache[4] = 
            (event) => {
              setValue('highlights', event.detail.value);
              validateHighlights(event);
            }
          )
        }, null, 8, ["labelText", "errorText", "placeholderText", "value", "isLoading"]),
        _createElementVNode("div", _hoisted_2, [
          _createVNode(AppButton, {
            size: "default",
            type: "primary",
            label: _unref(t)('resume.qualificationForm.saveBtnLabel'),
            ariaLabel: _unref(t)('resume.qualificationForm.saveBtnLabel'),
            onClick: saveForm,
            "data-test": "save-form-btn",
            class: "save-form-btn",
            width: "100%",
            isLoading: isFormSaving.value,
            disabled: !isFormSavingEnabled.value,
            ariaRoleDescription: saveButtonDescription.value
          }, null, 8, ["label", "ariaLabel", "isLoading", "disabled", "ariaRoleDescription"]),
          _createVNode(AppButton, {
            size: "default",
            type: "secondary",
            label: _unref(t)('resume.qualificationForm.deleteBtnLabel'),
            ariaLabel: _unref(t)('resume.qualificationForm.deleteBtnLabel'),
            "data-test": "delete-form-btn",
            class: "delete-form-btn",
            onClick: triggerDelete,
            isLoading: isQualificationDeleting.value,
            disabled: !qualificationId.value,
            width: "100%"
          }, null, 8, ["label", "ariaLabel", "isLoading", "disabled"]),
          _createVNode(AppButton, {
            size: "default",
            type: "textOnly",
            label: _unref(t)('resume.qualificationForm.closeBtnLabel'),
            ariaLabel: _unref(t)('resume.qualificationForm.closeBtnLabel'),
            "data-test": "close-form-btn",
            class: "close-form-btn",
            id: "closeBtn",
            onClick: returnToPage,
            width: "100%"
          }, null, 8, ["label", "ariaLabel"])
        ])
      ], 8, _hoisted_1)
    ]),
    "resume-helper": _withCtx(() => [...(_cache[5] || (_cache[5] = []))]),
    _: 1
  }, 8, ["breadcrumbs", "skipOnRouteBeforeLeave", "triggerDeleteModal"]))
}
}

})